<script setup lang="ts">
import type { IconName } from '@ui/components/icon/types'
import type { SizesType } from '@ui/styles'
import Icon from '@ui/components/icon/Icon.vue'
import { Sizes } from '@ui/styles'

const props = withDefaults(
  defineProps<{
    style?: 'contrast' | 'accent' | 'focus' | 'success' | string
    label?: string
    size?: SizesType
    src?: string
    icon?: IconName
  }>(),
  {
    style: 'contrast',
    label: '',
    size: Sizes.Obi,
    src: '',
    icon: '',
  },
)

const url = ref(props.src || '')
watch(
  () => props.src,
  () => {
    url.value = props.src
  },
)

function onError() {
  url.value = ''
}
</script>

<template lang="pug">
.avatar.flex-center-center.br-50p.flex-shrink-0(:class="[style, size]")
  slot
    img.image.s-100p.br-50p(v-if="url" :src="url" loading="lazy" :alt="label" @error="onError")
    Icon.icon(v-else-if="icon" :name="icon")
    .opacity-80(v-else) {{ label }}
</template>

<style scoped>
.avatar {
  color: var(--cue-content-primary-on-soft);
  border: 2px solid var(--cue-bg-surface-primary);
  margin: -1px;

  &.contrast {
    background: var(--cue-bg-primary-soft-dark);
  }

  &.accent {
    background: var(--cue-bg-accent-soft);
  }

  &.focus {
    background: var(--cue-bg-info-soft);
  }

  &.success {
    background: var(--cue-bg-success-soft);
  }

  &.r2d2 {
    --uno: typ-12-18-500 s-28;

    .icon {
      --uno: s-16;
    }
  }

  &.leia {
    --uno: typ-14-20-500 s-36;

    .icon {
      --uno: s-20;
    }
  }

  &.hanSolo {
    --uno: typ-16-24-500 s-40;

    .icon {
      --uno: s-24;
    }
  }

  &.luke {
    --uno: typ-18-26-500 s-48;

    .icon {
      --uno: s-24;
    }
  }

  &.obi {
    --uno: typ-18-26-500 s-56;

    .icon {
      --uno: s-24;
    }
  }

  &.ahsoka {
    --uno: typ-18-26-500 s-64;

    .icon {
      --uno: s-24;
    }
  }

  &.mandalorian {
    --uno: typ-22-24-500 s-100;

    .icon {
      --uno: s-26;
    }
  }

  &.vader {
    --uno: typ-32-36-500 s-120;

    .icon {
      --uno: s-32;
    }
  }

  &.palpatine {
    --uno: typ-36-40-500 s-168;

    .icon {
      --uno: s-40;
    }
  }
}

.image {
  object-fit: cover;
}
</style>
